import type { GameState } from '../game/GameStateMachine'
import type { LocalProfile, QualityPreference, ThemeId } from '../persistence/ProfileRepository'
import { COSMETICS, isCosmeticUnlocked, type ShellId, type TrailId } from '../content/CosmeticCatalog'
import type { RivalSnapshot } from '../rivals/RivalController'
import type { RecordedRun } from '../contracts/game'
import type { StoredReplay } from '../persistence/ReplayRepository'
import type { RunMode } from '../game/RunModes'

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector)
  if (element === null) throw new Error(`Missing required UI element: ${selector}`)
  return element
}

export interface UIEvents {
  readonly play: (mode: RunMode) => void
  readonly retry: () => void
  readonly pause: () => void
  readonly resume: () => void
  readonly menu: () => void
  readonly mute: () => void
  readonly profileChanged: (profile: LocalProfile) => void
  readonly loadReplays: () => Promise<readonly StoredReplay[]>
  readonly playReplay: (run: RecordedRun) => void
}

export class UIController {
  private readonly screens = new Map<string, HTMLElement>()
  private readonly distance = requireElement<HTMLElement>('#distance-value')
  private readonly deathDistance = requireElement<HTMLElement>('#death-distance')
  private readonly deathBest = requireElement<HTMLElement>('#death-best')
  private readonly menuBest = requireElement<HTMLElement>('#menu-best')
  private readonly newBest = requireElement<HTMLElement>('#new-best')
  private readonly milestone = requireElement<HTMLElement>('#milestone')
  private readonly settings = requireElement<HTMLDialogElement>('#settings-dialog')
  private readonly garage = requireElement<HTMLDialogElement>('#garage-dialog')
  private readonly replays = requireElement<HTMLDialogElement>('#replays-dialog')
  private readonly replayList = requireElement<HTMLElement>('#replay-list')
  private readonly shellGrid = requireElement<HTMLElement>('#shell-grid')
  private readonly trailGrid = requireElement<HTMLElement>('#trail-grid')
  private readonly garageShellName = requireElement<HTMLElement>('#garage-shell-name')
  private readonly rivalModeStatus = requireElement<HTMLElement>('#rival-mode-status')
  private readonly networkModeStatus = requireElement<HTMLElement>('#network-mode-status')
  private readonly rivalStandings = requireElement<HTMLOListElement>('#rival-standings')
  private readonly toast = requireElement<HTMLElement>('#status-toast')
  private readonly modeDetail = requireElement<HTMLElement>('#mode-detail')
  private readonly onProfileChanged: (profile: LocalProfile) => void
  private profile: LocalProfile
  private toastTimer: number | null = null
  private selectedMode: RunMode = 'endless'

  public constructor(profile: LocalProfile, events: UIEvents) {
    this.profile = profile
    this.onProfileChanged = events.profileChanged
    for (const element of document.querySelectorAll<HTMLElement>('[data-screen]')) {
      this.screens.set(element.dataset.screen ?? '', element)
    }
    requireElement<HTMLButtonElement>('#play-button').addEventListener('click', () => events.play(this.selectedMode))
    requireElement<HTMLButtonElement>('#retry-button').addEventListener('click', events.retry)
    requireElement<HTMLButtonElement>('#death-menu-button').addEventListener('click', events.menu)
    requireElement<HTMLButtonElement>('#pause-button').addEventListener('click', events.pause)
    requireElement<HTMLButtonElement>('#resume-button').addEventListener('click', events.resume)
    requireElement<HTMLButtonElement>('#pause-menu-button').addEventListener('click', events.menu)
    requireElement<HTMLButtonElement>('#audio-button').addEventListener('click', events.mute)
    requireElement<HTMLButtonElement>('#settings-button').addEventListener('click', () => this.settings.showModal())
    requireElement<HTMLButtonElement>('#garage-button').addEventListener('click', () => this.garage.showModal())
    requireElement<HTMLButtonElement>('#replays-button').addEventListener('click', () => { void this.openReplayBrowser(events) })
    for (const button of document.querySelectorAll<HTMLButtonElement>('[data-run-mode]')) {
      button.addEventListener('click', () => this.selectRunMode(button.dataset.runMode as RunMode))
    }
    this.populateGarage()
    this.bindSettings(events.profileChanged)
    this.applyProfile(profile)
    this.updateBest(profile.bestDistance)
  }

  public showState(state: GameState): void {
    const screen = state === 'MENU' ? 'menu'
      : state === 'PLAYING' || state === 'STARTING' ? 'hud'
        : state === 'DYING' || state === 'DEAD' ? 'death'
          : state === 'PAUSED' ? 'pause'
            : 'loading'
    for (const [id, element] of this.screens) element.classList.toggle('is-visible', id === screen)
  }

  public finishLoading(): void {
    this.showState('MENU')
  }

  public updateDistance(distance: number): void {
    this.distance.textContent = Math.floor(distance).toString().padStart(5, '0')
  }

  public updateBest(bestDistance: number): void {
    this.menuBest.textContent = `BEST // ${Math.floor(bestDistance).toString().padStart(5, '0')}`
  }

  public updateRivals(snapshots: readonly RivalSnapshot[], playerDistance: number): void {
    this.rivalStandings.replaceChildren()
    if (!this.profile.rivalsEnabled) return
    const racers = [
      { id: 'player', name: 'YOU', alive: true, distance: playerDistance },
      ...snapshots.map((snapshot) => ({
        id: snapshot.id,
        name: snapshot.name.split(' // ')[0] ?? snapshot.name,
        alive: snapshot.alive,
        distance: snapshot.distance,
      })),
    ].sort((a, b) => Number(b.alive) - Number(a.alive) || b.distance - a.distance)
    for (let index = 0; index < racers.length; index += 1) {
      const racer = racers[index]!
      const item = document.createElement('li')
      item.classList.toggle('is-player', racer.id === 'player')
      item.classList.toggle('is-eliminated', !racer.alive)
      const name = document.createElement('span')
      name.textContent = `${index + 1} // ${racer.name}`
      const distance = document.createElement('strong')
      distance.textContent = `${Math.floor(racer.distance).toString().padStart(5, '0')} M`
      item.append(name, distance)
      this.rivalStandings.append(item)
    }
  }

  public showDeath(distance: number, bestDistance: number, isNewBest: boolean): void {
    this.deathDistance.textContent = Math.floor(distance).toString().padStart(5, '0')
    this.deathBest.textContent = Math.floor(bestDistance).toString().padStart(5, '0')
    this.newBest.hidden = !isNewBest
  }

  public showMilestone(message: string): void {
    this.milestone.textContent = message
    window.setTimeout(() => { if (this.milestone.textContent === message) this.milestone.textContent = '' }, 1_800)
  }

  public showToast(message: string, durationMs = 3_000): void {
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer)
    this.toast.textContent = message
    this.toast.classList.add('is-visible')
    this.toastTimer = window.setTimeout(() => this.toast.classList.remove('is-visible'), durationMs)
  }

  public applyProfile(profile: LocalProfile): void {
    this.profile = profile
    document.documentElement.classList.toggle('reduced-motion', profile.reducedMotion)
    document.documentElement.classList.toggle('high-contrast', profile.highContrast)
    document.documentElement.dataset.theme = profile.theme
    const colors: Record<ThemeId, readonly [string, string]> = {
      ion: ['#42f5ff', '66, 245, 255'],
      reactor: ['#ffc84a', '255, 200, 74'],
      nova: ['#dd55ff', '221, 85, 255'],
      ghost: ['#f2fbff', '242, 251, 255'],
    }
    document.documentElement.style.setProperty('--accent', colors[profile.theme][0])
    document.documentElement.style.setProperty('--accent-rgb', colors[profile.theme][1])
    requireElement<HTMLInputElement>('#reduced-motion-setting').checked = profile.reducedMotion
    requireElement<HTMLInputElement>('#high-contrast-setting').checked = profile.highContrast
    requireElement<HTMLInputElement>('#bloom-setting').checked = profile.bloom
    requireElement<HTMLInputElement>('#drag-setting').checked = profile.dragSteering
    requireElement<HTMLInputElement>('#rivals-setting').checked = profile.rivalsEnabled
    requireElement<HTMLInputElement>('#online-setting').checked = profile.onlineEnabled
    const audioButton = requireElement<HTMLButtonElement>('#audio-button')
    audioButton.setAttribute('aria-label', profile.muted ? 'Unmute audio' : 'Mute audio')
    audioButton.textContent = profile.muted ? '○' : '◖'
    requireElement<HTMLInputElement>('#music-setting').value = profile.musicVolume.toString()
    requireElement<HTMLInputElement>('#sfx-setting').value = profile.sfxVolume.toString()
    requireElement<HTMLSelectElement>('#quality-setting').value = profile.quality
    this.refreshThemeOptions(profile)
    this.rivalModeStatus.textContent = `RIVALS // ${profile.rivalsEnabled ? 'AI CREW' : 'OFF'}`
    this.refreshGarage(profile)
  }

  public updateOnlineStatus(status: 'local' | 'disabled' | 'connecting' | 'online' | 'unavailable'): void {
    const labels = {
      local: 'LOCAL ONLY',
      disabled: 'OPT-IN OFF',
      connecting: 'CONNECTING',
      online: 'VERIFIED',
      unavailable: 'UNAVAILABLE',
    } as const
    this.networkModeStatus.textContent = `NETWORK // ${labels[status]}`
  }

  private bindSettings(onChanged: (profile: LocalProfile) => void): void {
    const emit = (): void => {
      const nextProfile: LocalProfile = {
        ...this.profile,
        theme: requireElement<HTMLSelectElement>('#theme-setting').value as ThemeId,
        quality: requireElement<HTMLSelectElement>('#quality-setting').value as QualityPreference,
        reducedMotion: requireElement<HTMLInputElement>('#reduced-motion-setting').checked,
        highContrast: requireElement<HTMLInputElement>('#high-contrast-setting').checked,
        bloom: requireElement<HTMLInputElement>('#bloom-setting').checked,
        dragSteering: requireElement<HTMLInputElement>('#drag-setting').checked,
        rivalsEnabled: requireElement<HTMLInputElement>('#rivals-setting').checked,
        onlineEnabled: requireElement<HTMLInputElement>('#online-setting').checked,
        musicVolume: Number(requireElement<HTMLInputElement>('#music-setting').value),
        sfxVolume: Number(requireElement<HTMLInputElement>('#sfx-setting').value),
      }
      this.applyProfile(nextProfile)
      onChanged(nextProfile)
    }
    for (const selector of [
      '#theme-setting', '#quality-setting', '#reduced-motion-setting', '#high-contrast-setting',
      '#bloom-setting', '#drag-setting', '#music-setting', '#sfx-setting',
      '#rivals-setting',
      '#online-setting',
    ]) requireElement<HTMLElement>(selector).addEventListener('change', emit)
  }

  private populateGarage(): void {
    for (const definition of COSMETICS.shells) {
      const button = this.createCosmeticButton(definition.id, definition.name, definition.description)
      button.addEventListener('click', () => this.selectShell(definition.id))
      this.shellGrid.append(button)
    }
    for (const definition of COSMETICS.trails) {
      const button = this.createCosmeticButton(definition.id, definition.name, definition.description)
      button.addEventListener('click', () => this.selectTrail(definition.id))
      this.trailGrid.append(button)
    }
  }

  private async openReplayBrowser(events: UIEvents): Promise<void> {
    this.replays.showModal()
    this.replayList.replaceChildren()
    const loading = document.createElement('p')
    loading.className = 'empty-state'
    loading.textContent = 'SCANNING LOCAL ARCHIVE…'
    this.replayList.append(loading)
    const entries = await events.loadReplays()
    this.replayList.replaceChildren()
    if (entries.length === 0) {
      loading.textContent = 'NO RUNS RECORDED'
      this.replayList.append(loading)
      return
    }
    for (const entry of entries) {
      const row = document.createElement('article')
      row.className = 'replay-row'
      const copy = document.createElement('div')
      const distance = document.createElement('strong')
      distance.textContent = `${Math.floor(entry.run.distance).toString().padStart(5, '0')} M`
      const metadata = document.createElement('small')
      metadata.textContent = `${new Date(entry.savedAt).toLocaleString()} // SEED ${entry.run.descriptor.seed.toString(16).toUpperCase()}`
      copy.append(distance, metadata)
      const watch = document.createElement('button')
      watch.type = 'button'
      watch.className = 'secondary-button'
      watch.textContent = 'Watch'
      watch.addEventListener('click', () => {
        this.replays.close()
        events.playReplay(entry.run)
      })
      row.append(copy, watch)
      this.replayList.append(row)
    }
  }

  private createCosmeticButton(id: string, name: string, description: string): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'cosmetic-card'
    button.dataset.cosmeticId = id
    const swatch = document.createElement('i')
    const title = document.createElement('strong')
    title.textContent = name
    const detail = document.createElement('small')
    detail.textContent = description
    button.append(swatch, title, detail)
    return button
  }

  private selectShell(id: ShellId): void {
    const definition = COSMETICS.shells.find((entry) => entry.id === id)
    if (definition === undefined || !isCosmeticUnlocked(definition, this.profile)) {
      this.showToast('LOCKED // COMPLETE ITS MASTERY REQUIREMENT')
      return
    }
    const next = { ...this.profile, shell: id }
    this.applyProfile(next)
    this.onProfileChanged(next)
  }

  private selectTrail(id: TrailId): void {
    const definition = COSMETICS.trails.find((entry) => entry.id === id)
    if (definition === undefined || !isCosmeticUnlocked(definition, this.profile)) {
      this.showToast('LOCKED // COMPLETE ITS MASTERY REQUIREMENT')
      return
    }
    const next = { ...this.profile, trail: id }
    this.applyProfile(next)
    this.onProfileChanged(next)
  }

  private refreshGarage(profile: LocalProfile): void {
    const unlockState = { bestDistance: profile.bestDistance, mastery: profile.mastery }
    for (const definition of COSMETICS.shells) {
      const button = this.shellGrid.querySelector<HTMLButtonElement>(`[data-cosmetic-id="${definition.id}"]`)
      if (button === null) continue
      button.classList.toggle('is-equipped', definition.id === profile.shell)
      button.classList.toggle('is-locked', !isCosmeticUnlocked(definition, unlockState))
    }
    for (const definition of COSMETICS.trails) {
      const button = this.trailGrid.querySelector<HTMLButtonElement>(`[data-cosmetic-id="${definition.id}"]`)
      if (button === null) continue
      button.classList.toggle('is-equipped', definition.id === profile.trail)
      button.classList.toggle('is-locked', !isCosmeticUnlocked(definition, unlockState))
    }
    this.garageShellName.textContent = COSMETICS.shells.find((entry) => entry.id === profile.shell)?.name.toUpperCase() ?? 'ION CORE'
  }

  private refreshThemeOptions(profile: LocalProfile): void {
    const select = requireElement<HTMLSelectElement>('#theme-setting')
    for (const option of select.options) option.disabled = !profile.unlocks.includes(option.value as never)
    if (!profile.unlocks.includes(profile.theme)) select.value = 'ion'
    else select.value = profile.theme
  }

  private selectRunMode(mode: RunMode): void {
    this.selectedMode = mode
    for (const button of document.querySelectorAll<HTMLButtonElement>('[data-run-mode]')) {
      button.classList.toggle('is-selected', button.dataset.runMode === mode)
    }
    const descriptions: Readonly<Record<RunMode, string>> = {
      endless: 'FRESH SEED // PERSONAL BEST',
      daily: 'ONE UTC SEED // VERIFIED DAILY BOARD',
      apex: 'FIXED CHAMPIONSHIP SEED // GLOBAL BOARD',
    }
    this.modeDetail.textContent = descriptions[mode]
  }
}
